import createCommands from './createCommands';
import { getNavigationCommands, getSettingsCommands } from './navConfig';
import { PLATFORMS } from '../platform/platformConfig';

export const COMMAND_GROUPS = {
  CREATE: 'Create',
  SUGGESTED: 'Suggested',
  NAVIGATION: 'Go to',
  SETTINGS: 'Settings',
  ACTIONS: 'Quick actions',
};

export const ACTION_TYPES = {
  SCHEDULE_MEETING: 'scheduleMeeting',
  TOGGLE_THEME: 'toggleTheme',
  OPEN_SUPPORT: 'openSupport',
  OPEN_FEEDBACK: 'openFeedback',
  COPY_PAGE_LINK: 'copyPageLink',
  OPEN_HELP_CHAT: 'openHelpChat',
  START_PAGE_TOUR: 'startPageTour',
  START_WELCOME_TOUR: 'startWelcomeTour',
  LOGOUT: 'logout',
};

const GROUP_ORDER_DEFAULT = [
  COMMAND_GROUPS.CREATE,
  COMMAND_GROUPS.SUGGESTED,
  COMMAND_GROUPS.NAVIGATION,
  COMMAND_GROUPS.SETTINGS,
  COMMAND_GROUPS.ACTIONS,
];

const GROUP_ORDER_SEARCH = [
  COMMAND_GROUPS.CREATE,
  COMMAND_GROUPS.SUGGESTED,
  COMMAND_GROUPS.NAVIGATION,
  COMMAND_GROUPS.SETTINGS,
  COMMAND_GROUPS.ACTIONS,
];

export { GROUP_ORDER_DEFAULT, GROUP_ORDER_SEARCH };

const EXCLUDED_COMMAND_PATH_PREFIXES = ['/aiengagement'];

function getCommandLabelText(label) {
  if (typeof label === 'string') return label;
  return '';
}

export function isExcludedCommand(command) {
  if (!command) return true;

  const id = String(command.id || '').toLowerCase();
  const path = command.path || '';
  const label = getCommandLabelText(command.label).toLowerCase();
  const description = (command.description || '').toLowerCase();

  if (
    id === 'aiengagement' ||
    id.startsWith('aiengagement-') ||
    id.startsWith('create-aiengagement')
  ) {
    return true;
  }

  if (
    EXCLUDED_COMMAND_PATH_PREFIXES.some(prefix => path === prefix || path.startsWith(`${prefix}/`))
  ) {
    return true;
  }

  if (label.includes('ai engagements') || label.includes('ai engagement')) {
    return true;
  }

  if (description.includes('ai-powered engagement') || description.includes('ai engagement')) {
    return true;
  }

  return false;
}

function filterExcludedCommands(commands) {
  return commands.filter(command => !isExcludedCommand(command));
}

const extraNavigationCommands = [
  {
    id: 'invite-team',
    label: 'Invite Team Member',
    description: 'Add someone new to your workspace',
    group: COMMAND_GROUPS.NAVIGATION,
    path: '/team',
    sectionname: null,
    icon: 'UserGroupOutlined',
    keywords: ['invite', 'team', 'member', 'collaborator', 'add user'],
  },
  {
    id: 'change-password',
    label: 'Change Password',
    description: 'Update your account password',
    group: COMMAND_GROUPS.NAVIGATION,
    path: '/security',
    sectionname: null,
    icon: 'LockOutlined',
    keywords: ['password', 'security', 'reset', 'credentials'],
  },
];

const quickActionCommands = [
  {
    id: 'start-page-tour',
    label: 'Take a tour of this page',
    description: 'A short guided walkthrough of the page you are on',
    group: COMMAND_GROUPS.ACTIONS,
    actionType: ACTION_TYPES.START_PAGE_TOUR,
    sectionname: null,
    icon: 'PlayCircleOutlined',
    keywords: ['tour', 'guide', 'walkthrough', 'onboarding', 'help', 'learn', 'how'],
  },
  {
    id: 'start-welcome-tour',
    label: 'Take the welcome tour',
    description: 'See where everything lives in the dashboard',
    group: COMMAND_GROUPS.ACTIONS,
    actionType: ACTION_TYPES.START_WELCOME_TOUR,
    sectionname: null,
    icon: 'RocketOutlined',
    keywords: ['tour', 'welcome', 'onboarding', 'getting started', 'guide', 'new'],
  },
  {
    id: 'copy-page-link',
    label: 'Copy Current Page Link',
    description: 'Copy this page URL to your clipboard',
    group: COMMAND_GROUPS.ACTIONS,
    actionType: ACTION_TYPES.COPY_PAGE_LINK,
    sectionname: null,
    icon: 'CopyOutlined',
    keywords: ['copy', 'link', 'url', 'share', 'clipboard'],
  },
  {
    id: 'open-help-chat',
    label: 'Open Help Chat',
    description: 'Chat with our documentation assistant',
    group: COMMAND_GROUPS.ACTIONS,
    actionType: ACTION_TYPES.OPEN_HELP_CHAT,
    sectionname: null,
    icon: 'ChatFilled',
    keywords: ['help', 'chat', 'assistant', 'docs', 'documentation'],
  },
  {
    id: 'contact-support',
    label: 'Contact Support',
    description: 'Email our support team for help',
    group: COMMAND_GROUPS.ACTIONS,
    actionType: ACTION_TYPES.OPEN_SUPPORT,
    sectionname: null,
    icon: 'QuestionOutlined',
    keywords: ['support', 'help', 'contact', 'issue', 'problem'],
  },
  {
    id: 'send-feedback',
    label: 'Send Feedback',
    description: 'Share product feedback with our team',
    group: COMMAND_GROUPS.ACTIONS,
    actionType: ACTION_TYPES.OPEN_FEEDBACK,
    sectionname: null,
    icon: 'LikeOutlined',
    keywords: ['feedback', 'suggestion', 'idea', 'improve', 'review'],
  },
  {
    id: 'schedule-meeting',
    label: 'Schedule a Meeting',
    description: 'Book time with the Bridged team',
    group: COMMAND_GROUPS.ACTIONS,
    actionType: ACTION_TYPES.SCHEDULE_MEETING,
    sectionname: null,
    icon: 'CalendarOutlined',
    keywords: ['schedule', 'meeting', 'call', 'book', 'demo'],
  },
  {
    id: 'toggle-theme',
    label: 'Toggle Theme',
    description: 'Switch between light and dark mode',
    group: COMMAND_GROUPS.ACTIONS,
    actionType: ACTION_TYPES.TOGGLE_THEME,
    sectionname: null,
    icon: 'MoonOutlined',
    keywords: ['theme', 'dark', 'light', 'mode', 'appearance'],
  },
  {
    id: 'logout',
    label: 'Log Out',
    description: 'Sign out of your account',
    group: COMMAND_GROUPS.ACTIONS,
    actionType: ACTION_TYPES.LOGOUT,
    sectionname: null,
    icon: 'LogoutOutlined',
    keywords: ['logout', 'sign out', 'exit', 'leave'],
  },
];

const contextualRouteMap = [
  {
    prefix: '/agents/',
    dashboardPath: '/agents/dashboard',
    label: 'Back to Agents',
    description: 'Return to the agents dashboard',
    sectionname: 'campaigns',
    icon: 'LineChartOutlined',
    keywords: ['back', 'agents'],
  },
  {
    exactPath: '/agents-draft',
    dashboardPath: '/agents/dashboard',
    label: 'Back to Agents',
    description: 'Return to the agents dashboard',
    sectionname: 'campaigns',
    icon: 'LineChartOutlined',
    keywords: ['back', 'agents', 'draft'],
  },
  {
    prefix: '/engagement/',
    dashboardPath: '/engagement/dashboard',
    label: 'Back to Engagements',
    description: 'Return to the engagements dashboard',
    sectionname: 'screens',
    icon: 'ScreenOutlined',
    keywords: ['back', 'engagement'],
  },
  {
    prefix: '/cta/',
    dashboardPath: '/cta/dashboard',
    label: 'Back to CTAs',
    description: 'Return to the CTA dashboard',
    sectionname: 'screens',
    icon: 'ScreenOutlined',
    keywords: ['back', 'cta'],
  },
  {
    prefix: '/endscreen/',
    dashboardPath: '/endscreen/dashboard',
    label: 'Back to End Screens',
    description: 'Return to the end screens dashboard',
    sectionname: 'screens',
    icon: 'ScreenOutlined',
    keywords: ['back', 'end screen'],
  },
  {
    prefix: '/recirculations/',
    dashboardPath: '/recirculations/dashboard',
    label: 'Back to Recirculation',
    description: 'Return to the Recirculation dashboard',
    sectionname: 'screens',
    icon: 'FolderOutlined',
    keywords: ['back', 'collections', 'recirculation'],
  },
  {
    prefix: '/collections-v1/',
    dashboardPath: '/recirculations/dashboard',
    label: 'Back to Recirculation',
    description: 'Return to the Recirculation dashboard',
    sectionname: 'screens',
    icon: 'FolderOutlined',
    keywords: ['back', 'collections', 'v1', 'recirculation'],
  },
  {
    prefix: '/collections/',
    dashboardPath: '/collections/dashboard',
    label: 'Back to Event Database',
    description: 'Return to the Event Database',
    sectionname: 'screens',
    icon: 'FolderOutlined',
    keywords: ['back', 'collections'],
  },
  {
    prefix: '/aiLabs/',
    dashboardPath: '/aiLabs/dashboard',
    label: 'Back to AI Engines',
    description: 'Return to the AI Labs engines dashboard',
    sectionname: 'aiLabs',
    icon: 'AIStarOutlined',
    keywords: ['back', 'ai labs', 'engines'],
  },
  {
    prefix: '/fine-tuning/',
    dashboardPath: '/fine-tuning/dashboard',
    label: 'Back to Fine Tuning',
    description: 'Return to the fine tuning dashboard',
    sectionname: 'aiLabs',
    icon: 'AIStarOutlined',
    keywords: ['back', 'fine tuning'],
  },
  {
    prefix: '/sponsors/',
    dashboardPath: '/sponsors',
    label: 'Back to Sponsors',
    description: 'Return to the sponsors dashboard',
    sectionname: 'sponsors',
    icon: 'DollarSignOutlined',
    keywords: ['back', 'sponsors'],
    requiresSponsorsActive: true,
  },
];

const isEventsCollectionsRoute = route =>
  route.prefix === '/collections/' || route.dashboardPath === '/collections/dashboard';

export function getContextualCommands(pathname, { isSponsorsActive, platform }) {
  const commands = [];
  const isEventsPlatform = platform === PLATFORMS.EVENTS;

  contextualRouteMap.forEach(route => {
    if (route.requiresSponsorsActive && !isSponsorsActive) return;

    const dashboardPath =
      isEventsPlatform && route.prefix === '/agents/' ? '/events' : route.dashboardPath;
    const label =
      isEventsPlatform && route.prefix === '/agents/'
        ? 'Back to Events'
        : isEventsPlatform && isEventsCollectionsRoute(route)
          ? 'Back to Event Database'
          : route.label;
    const description =
      isEventsPlatform && route.prefix === '/agents/'
        ? 'Return to events and workflows'
        : isEventsPlatform && isEventsCollectionsRoute(route)
          ? 'Return to the Event Database'
          : route.description;

    const isSubRoute = route.exactPath
      ? pathname === route.exactPath
      : pathname.startsWith(route.prefix) && pathname !== dashboardPath;

    if (isSubRoute) {
      commands.push({
        id: route.exactPath ? `context-${route.exactPath}` : `context-${route.prefix}`,
        label,
        description,
        group: COMMAND_GROUPS.SUGGESTED,
        path: dashboardPath,
        sectionname: route.sectionname,
        icon: route.icon,
        keywords: route.keywords,
        requiresSponsorsActive: route.requiresSponsorsActive || false,
      });
    }
  });

  if (pathname && pathname !== '/') {
    commands.push({
      id: 'context-home',
      label: 'Go to Home Dashboard',
      description: 'Return to the main dashboard overview',
      group: COMMAND_GROUPS.SUGGESTED,
      path: '/',
      sectionname: 'dashboard',
      icon: 'AppStoreOutlined',
      keywords: ['home', 'dashboard', 'overview'],
    });
  }

  return filterExcludedCommands(commands);
}

export function getAllCommands(platform) {
  const commands = [
    ...createCommands,
    ...getNavigationCommands(platform),
    ...getSettingsCommands(),
    ...extraNavigationCommands,
    ...quickActionCommands,
  ];

  const filtered =
    platform === PLATFORMS.EVENTS
      ? commands.filter(command => {
          if (command.path === '/agents/dashboard' || command.path === '/agents-draft')
            return false;
          return true;
        })
      : commands;

  return filterExcludedCommands(filtered);
}

export function enrichCommand(command, { isDarkTheme }) {
  if (command.actionType !== ACTION_TYPES.TOGGLE_THEME) {
    return command;
  }

  return {
    ...command,
    label: isDarkTheme ? 'Switch to Light Mode' : 'Switch to Dark Mode',
    description: isDarkTheme ? 'Use the light colour theme' : 'Use the dark colour theme',
    icon: isDarkTheme ? 'SunOutlined' : 'MoonOutlined',
  };
}
